import React, { useState, useMemo } from 'react';
import { Download, ChevronLeft, ChevronRight } from 'lucide-react';
import type { PlanDownloadState, PlanDownloadPaginationInfo } from '../../../../types/planDownload';
import { mockPlanData } from '../../../../data/mockPlanData';
import { exportPlanDataToExcel } from '../../../../utils/excelExport';
import '../../../../styles/planDownload.css';

interface PlanDownloadProps {
  className?: string;
}

const PlanDownload: React.FC<PlanDownloadProps> = ({ className = '' }) => {
  const [state, setState] = useState<PlanDownloadState>({
    planData: mockPlanData,
    effectiveDateFrom: '',
    effectiveDateTo: '',
    activePremiumOnly: true,
    currentPage: 1,
    itemsPerPage: 10,
    totalRecords: 0,
    isLoading: false,
    hasDateSelection: false
  });

  // Filter data based on date selection and premium filter
  const filteredData = useMemo(() => {
    if (!state.hasDateSelection) {
      return [];
    }

    let filtered = state.planData;

    // Apply date range filter
    if (state.effectiveDateFrom && state.effectiveDateTo) {
      const fromDate = new Date(state.effectiveDateFrom);
      const toDate = new Date(state.effectiveDateTo);
      
      filtered = filtered.filter(item => {
        const effectiveDate = new Date(item.effectiveDate);
        return effectiveDate >= fromDate && effectiveDate <= toDate;
      });
    }

    // Apply premium filter if needed
    if (state.activePremiumOnly) {
      // Filter for active premium (grossPremium > 0)
      filtered = filtered.filter(item => item.grossPremium > 0);
    }

    return filtered;
  }, [state.planData, state.effectiveDateFrom, state.effectiveDateTo, state.activePremiumOnly, state.hasDateSelection]);

  // Calculate pagination
  const paginationInfo = useMemo((): PlanDownloadPaginationInfo => {
    const totalRecords = filteredData.length;
    const totalPages = Math.ceil(totalRecords / state.itemsPerPage);
    const startRecord = (state.currentPage - 1) * state.itemsPerPage + 1;
    const endRecord = Math.min(state.currentPage * state.itemsPerPage, totalRecords);

    return {
      currentPage: state.currentPage,
      totalPages,
      totalRecords,
      itemsPerPage: state.itemsPerPage,
      startRecord: totalRecords > 0 ? startRecord : 0,
      endRecord: totalRecords > 0 ? endRecord : 0
    };
  }, [filteredData.length, state.currentPage, state.itemsPerPage]);

  // Get current page data
  const currentPageData = useMemo(() => {
    const startIndex = (state.currentPage - 1) * state.itemsPerPage;
    const endIndex = startIndex + state.itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, state.currentPage, state.itemsPerPage]);

  const handleDateChange = (field: 'effectiveDateFrom' | 'effectiveDateTo', value: string) => {
    setState(prev => {
      const newState = {
        ...prev,
        [field]: value,
        currentPage: 1 // Reset to first page when filtering
      };
      
      // Check if both dates are selected
      const hasDateSelection = (field === 'effectiveDateFrom' ? value : prev.effectiveDateFrom) && 
                              (field === 'effectiveDateTo' ? value : prev.effectiveDateTo);
      
      return {
        ...newState,
        hasDateSelection: !!hasDateSelection
      };
    });
  };

  const handlePremiumFilterChange = (value: boolean) => {
    setState(prev => ({
      ...prev,
      activePremiumOnly: value,
      currentPage: 1
    }));
  };

  const handlePageChange = (page: number) => {
    setState(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  const handleDownload = () => {
    if (!state.hasDateSelection || filteredData.length === 0) {
      return;
    }

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      exportPlanDataToExcel(
        filteredData,
        state.effectiveDateFrom,
        state.effectiveDateTo,
        state.activePremiumOnly
      );
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export data to Excel. Please try again.');
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const renderPagination = () => {
    if (!state.hasDateSelection || paginationInfo.totalRecords === 0) {
      return null;
    }

    return (
      <div className="plan-download-pagination">
        <div className="pagination-info">
          Showing {paginationInfo.startRecord} to {paginationInfo.endRecord} of {paginationInfo.totalRecords} records
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(paginationInfo.currentPage - 1)}
            disabled={paginationInfo.currentPage === 1}
          >
            <ChevronLeft size={16} />
          </button>
          
          {Array.from({ length: paginationInfo.totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`pagination-number ${page === paginationInfo.currentPage ? 'active' : ''}`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}
          
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(paginationInfo.currentPage + 1)}
            disabled={paginationInfo.currentPage === paginationInfo.totalPages}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  };

  const renderDataPreview = () => {
    if (!state.hasDateSelection) {
      return null;
    }

    return (
      <div className="data-preview-section">
        <div className="data-preview-header">
          <h2 className="data-preview-title">Data Preview</h2>
          <div className="records-count">
            {paginationInfo.totalRecords} records found
          </div>
        </div>

        <div className="plan-data-download-table-container">
          <table className="plan-data-download-table">
            <thead className="plan-data-download-table-header">
              <tr>
                <th>PRODUCT CODE</th>
                <th>PLAN CODE</th>
                <th>HBS PLAN ID</th>
                <th>HBS REV NO</th>
                <th>PLAN DESCRIPTION</th>
                <th>EFFECTIVE DATE</th>
                <th>EXPIRATION DATE</th>
                <th>MIN DAYS</th>
                <th>MAX DAYS</th>
                <th>CURRENCY</th>
                <th>GROSS PREMIUM</th>
                <th>NET PREMIUM</th>
                <th>MRL</th>
                <th>COVERAGE CODE</th>
                <th>AVAILABLE FOR SALES AGENT</th>
                <th>OCC TEMPLATE</th>
                <th>IS CANCELLED</th>
                <th>OCC PREFIX</th>
                <th>TACTIC TEMPLATE</th>
                <th>APP FORM TEMPLATE</th>
                <th>PLAN TAX</th>
                <th>LOCAL TAX</th>
                <th>DST</th>
              </tr>
            </thead>
            <tbody className="plan-data-download-table-body">
              {currentPageData.map((item) => (
                <tr key={item.id} className="plan-data-row">
                  <td>{item.productCode}</td>
                  <td>{item.planCode}</td>
                  <td>{item.hisPlanId}</td>
                  <td>{item.hisRevNo}</td>
                  <td>{item.planDescription}</td>
                  <td>{item.effectiveDate}</td>
                  <td>{item.expirationDate}</td>
                  <td>{item.minimumDays}</td>
                  <td>{item.maximumDays}</td>
                  <td>{item.currency}</td>
                  <td>{item.grossPremium.toFixed(2)}</td>
                  <td>{item.netPremium}</td>
                  <td>{item.mrl}</td>
                  <td>{item.coverageCode}</td>
                  <td>{item.availableForSalesAgent ? 'TRUE' : 'FALSE'}</td>
                  <td>{item.occTemplate}</td>
                  <td>{item.isCancelled ? 'TRUE' : 'FALSE'}</td>
                  <td>{item.occPrefix}</td>
                  <td>{item.tacticTemplate}</td>
                  <td>{item.appFormTemplate}</td>
                  <td>{item.planTax}</td>
                  <td>{item.localTax}</td>
                  <td>{item.gst}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {renderPagination()}
      </div>
    );
  };

  return (
    <div className={`plan-download ${className}`}>
      {/* Header */}
      <div className="plan-download-header">
        <div className="plan-download-header-left">
          <div className="plan-download-icon">
            <Download size={24} />
          </div>
          <div className="plan-download-title-section">
            <h1 className="plan-download-title">Plan Download</h1>
            <div className="plan-download-context">
              Generate and download plan reports for specified date ranges
            </div>
          </div>
        </div>
        
        <div className="plan-download-header-actions">
          <button
            className="btn-download-plan"
            onClick={handleDownload}
            disabled={!state.hasDateSelection || filteredData.length === 0 || state.isLoading}
          >
            <Download size={16} />
            {state.isLoading ? 'Generating...' : 'Download'}
          </button>
        </div>
      </div>

      {/* Date Filter Section */}
      <div className="plan-download-filter-section">
        <div className="filter-container">
          <div className="date-filter-group">
            <label className="date-filter-label">Effective date from</label>
            <input
              type="date"
              className="date-input"
              value={state.effectiveDateFrom}
              onChange={(e) => handleDateChange('effectiveDateFrom', e.target.value)}
            />
            <span className="date-separator">to</span>
            <input
              type="date"
              className="date-input"
              value={state.effectiveDateTo}
              onChange={(e) => handleDateChange('effectiveDateTo', e.target.value)}
            />
          </div>
          
          <div className="premium-filter-group">
            <label className="premium-filter-label">Active premium only?</label>
            <div className="radio-group-plan-download">
              <div className="radio-option-plan-download">
                <input
                  type="radio"
                  id="premium-yes"
                  name="activePremium"
                  className="radio-input"
                  checked={state.activePremiumOnly === true}
                  onChange={() => handlePremiumFilterChange(true)}
                />
                <label htmlFor="premium-yes" className="radio-option-plan-download label">Yes</label>
              </div>
              <div className="radio-option-plan-download">
                <input
                  type="radio"
                  id="premium-no"
                  name="activePremium"
                  className="radio-input"
                  checked={state.activePremiumOnly === false}
                  onChange={() => handlePremiumFilterChange(false)}
                />
                <label htmlFor="premium-no" className="radio-option-plan-download label">No</label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Preview */}
      {renderDataPreview()}
    </div>
  );
};

export default PlanDownload;